import { FlatQuotationLine, QuotedColorGroup, SmallLotPricing } from '@ravago/shared/page-data/models/elements';
import { Address } from '@ravago/shared/page-data/models/elements/address.model';
import { LeadTimeIndicator } from '@ravago/shared/page-data/models/elements/product-lead-time.model';

import { DEFAULT_PRODUCER_COLOR_CODE } from '../../components/content/dynamic-product-block/constants';

export type LeadTimeCheckCriteria = AddressLeadTimeCheckCriteria | CountryLeadTimeCheckCriteria | BaseLeadTimeCheckCriteria;

export interface BaseLeadTimeCheckCriteria {
   colorId: number;
   producerColorCode?: string;
   packagingGroupId?: number;
}

export interface AddressLeadTimeCheckCriteria extends BaseLeadTimeCheckCriteria {
   addressId: number;
   countryCode?: never;
}

export interface CountryLeadTimeCheckCriteria extends BaseLeadTimeCheckCriteria {
   countryCode: string;
   addressId?: never;
}

export interface LeadTimeFilterCriteria {
   packagingGroups: Array<{ id: number }>;
   addresses?: Array<{ id: number }>;
   countries?: Array<{ code: string }>;
}

const normalizeCountryCode = (countryCode: string): string => (countryCode === 'GB' ? 'UK' : countryCode.toUpperCase());

const matchesColor = (indicator: LeadTimeIndicator, criteria: LeadTimeCheckCriteria): boolean => {
   if (criteria.producerColorCode !== DEFAULT_PRODUCER_COLOR_CODE && criteria.producerColorCode !== undefined) {
      return indicator.color.id === criteria.colorId && indicator.color.producerColorCode === criteria.producerColorCode;
   }
   return indicator.color.id === criteria.colorId;
};

const matchesPackaging = (indicator: LeadTimeIndicator, criteria: LeadTimeCheckCriteria): boolean => {
   if (criteria.packagingGroupId === undefined) {
      return true;
   }
   return indicator.packagingGroupId === criteria.packagingGroupId;
};

const matchesAddress = (indicator: LeadTimeIndicator, criteria: AddressLeadTimeCheckCriteria): boolean => {
   if (criteria.addressId === undefined) {
      return true;
   }
   return indicator.addressId === criteria.addressId;
};

const matchesCountry = (indicator: LeadTimeIndicator, criteria: CountryLeadTimeCheckCriteria): boolean => {
   if (criteria.countryCode === undefined) {
      return true;
   }
   const normalizedCriteriaCountryCode = normalizeCountryCode(criteria.countryCode);
   return indicator.countryCode === normalizedCriteriaCountryCode;
};

const hasMtsLocalStockingPolicy = (indicators: LeadTimeIndicator[]): boolean =>
   indicators.some((indicator) => indicator.stockingPolicy === 'MTS LOCAL');

const filterByCriteria = (indicators: LeadTimeIndicator[], criteria: LeadTimeCheckCriteria): LeadTimeIndicator[] =>
   indicators.filter((indicator) => {
      if (!matchesColor(indicator, criteria) || !matchesPackaging(indicator, criteria)) return false;
      if ('addressId' in criteria && criteria.addressId !== undefined) return matchesAddress(indicator, criteria);
      if ('countryCode' in criteria && criteria.countryCode !== undefined) return matchesCountry(indicator, criteria);
      return true;
   });

/**
 * Checks if there is MTS LOCAL availability for the given criteria.
 *
 * When both `addressId` and `countryCode` are provided the function performs a
 * PSP (address-based) check first and falls back to a country-based check —
 * this is the verified-customer address flow.
 *
 * @param indicators - Array of lead time indicators (already pre-filtered to the relevant set)
 * @param criteria - The criteria to check against
 * @returns true if MTS LOCAL is available, false otherwise
 */
export const hasMtsLocalLeadTime = (indicators: LeadTimeIndicator[], criteria: LeadTimeCheckCriteria): boolean => {
   if (indicators.length === 0) {
      return false;
   }

   return hasMtsLocalStockingPolicy(filterByCriteria(indicators, criteria));
};

export const hasQuotedColorGroupMtsLocalLeadTime = (
   indicators: LeadTimeIndicator[],
   group: QuotedColorGroup,
   { bracketPricingEnabled = false, addressId }: { bracketPricingEnabled?: boolean; addressId?: number } = {}
): boolean =>
   group.quotedColors.some(({ quotation }) => {
      const quoteAddressId = quotation.deliveryInformation?.address?.id;
      if (quoteAddressId === undefined || (addressId !== undefined && quoteAddressId !== addressId)) return false;

      return quotation.lines.some((line) => {
         const packagingGroupId = bracketPricingEnabled && line.bracketPricing ? line.bracketPricing.packaging.group.id : line.packagingGroupId;
         if (packagingGroupId === undefined) return false;

         return hasMtsLocalLeadTime(indicators, {
            colorId: group.colorId,
            producerColorCode: group.producerColorCode,
            addressId: quoteAddressId,
            packagingGroupId
         });
      });
   });

export const hasQuotedColorLineMtsLocalLeadTime = (
   indicators: LeadTimeIndicator[],
   group: QuotedColorGroup,
   lineId: string,
   { bracketPricingEnabled = false }: { bracketPricingEnabled?: boolean } = {}
): boolean =>
   group.quotedColors.some(({ quotation }) => {
      const quoteAddressId = quotation.deliveryInformation?.address?.id;
      if (quoteAddressId === undefined) return false;

      return quotation.lines.some((line) => {
         if (line.id !== lineId) return false;

         const packagingGroupId = bracketPricingEnabled && line.bracketPricing ? line.bracketPricing.packaging.group.id : line.packagingGroupId;
         if (packagingGroupId === undefined) return false;

         return hasMtsLocalLeadTime(indicators, {
            colorId: group.colorId,
            producerColorCode: group.producerColorCode,
            addressId: quoteAddressId,
            packagingGroupId
         });
      });
   });

export const hasSmallLotMtsLocalLeadTime = (indicators: LeadTimeIndicator[], pricing: SmallLotPricing, address: Address): boolean => {
   if (pricing.packaging.packagingGroupId === undefined) return false;

   const criteria = {
      colorId: pricing.color.id,
      producerColorCode: pricing.color.producerColorCode || undefined,
      packagingGroupId: pricing.packaging.packagingGroupId
   };

   return (
      hasMtsLocalLeadTime(indicators, { ...criteria, addressId: address.id }) ||
      hasMtsLocalLeadTime(indicators, { ...criteria, countryCode: address.countryIsoCode })
   );
};

/**
 * Builds a lineId -> hasMtsLocal map for a set of flattened quote lines.
 */
export const getQuoteLineMtsLocalMap = (
   indicators: LeadTimeIndicator[],
   lines: FlatQuotationLine[],
   criteria: { colorId?: number; producerColorCode?: string; addressId?: number }
): Record<string, boolean> => {
   const { colorId, addressId, producerColorCode } = criteria;
   if (colorId === undefined || addressId === undefined) return {};

   return Object.fromEntries(
      lines
         .filter((line): line is FlatQuotationLine & { linePackagingGroupId: number } => line.linePackagingGroupId !== undefined)
         .map((line) => [
            line.lineId,
            hasMtsLocalLeadTime(indicators, {
               colorId,
               producerColorCode,
               packagingGroupId: line.linePackagingGroupId,
               addressId
            })
         ])
   );
};

/**
 * Filters lead time indicators based on available packaging groups and addresses/countries
 * @param indicators - Array of lead time indicators
 * @param filterCriteria - The filter criteria containing packaging groups and addresses or countries
 * @returns Filtered array of lead time indicators
 */
export const filterLeadTimeIndicators = (indicators: LeadTimeIndicator[], filterCriteria: LeadTimeFilterCriteria): LeadTimeIndicator[] => {
   if (indicators.length === 0) {
      return [];
   }

   return indicators.filter((indicator) => {
      const packagingGroupMatch = filterCriteria.packagingGroups.some((group) => group.id === indicator.packagingGroupId);
      if (!packagingGroupMatch) {
         return false;
      }

      if (filterCriteria.addresses && filterCriteria.addresses.length > 0) {
         return filterCriteria.addresses.some((address) => address.id === indicator.addressId);
      }

      if (filterCriteria.countries && filterCriteria.countries.length > 0) {
         return filterCriteria.countries.some((country) => normalizeCountryCode(country.code) === indicator.countryCode);
      }

      return true;
   });
};

/**
 * - Verified customers: unions PSP-filtered (address-based) and guest-fallback-filtered
 *   (country codes derived from their addresses) indicators so that products with no PSP
 *   record can still surface a lead time via the guest-logic fallback.
 * - Guests / pending users: straight country-based filter.
 *
 * @param indicators        - Raw lead time indicators from the API (already MTS LOCAL only).
 * @param packagingGroups   - Packaging groups for the current product.
 * @param customerAddresses - Delivery addresses of the verified customer, or undefined for guests.
 * @param countries         - Country codes to use for guest-logic filtering.
 * @returns The filtered subset of indicators.
 */
export const getFilteredLeadTimeIndicators = (
   indicators: LeadTimeIndicator[],
   packagingGroups: Array<{ id: number }>,
   customerAddresses: Address[] | undefined,
   countries: Array<{ code: string }>
): LeadTimeIndicator[] => {
   if (indicators.length === 0 || packagingGroups.length === 0) return [];

   if (customerAddresses) {
      const pspFiltered = filterLeadTimeIndicators(indicators, { packagingGroups, addresses: customerAddresses });

      const uniqueCountries = [...new Set(customerAddresses.map(({ countryIsoCode }) => countryIsoCode))].map((code) => ({ code }));
      const guestFallbackFiltered = filterLeadTimeIndicators(indicators, { packagingGroups, countries: uniqueCountries });

      const combined = [...pspFiltered];
      for (const indicator of guestFallbackFiltered) {
         if (!combined.includes(indicator)) combined.push(indicator);
      }
      return combined;
   }

   return filterLeadTimeIndicators(indicators, { packagingGroups, countries });
};
